import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { NgComponentOutlet } from '@angular/common';
import { LucideCalendarDays } from '@lucide/angular';
import flatpickr from 'flatpickr';
import { Indonesian } from 'flatpickr/dist/l10n/id';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

/**
 * Flatpickr-backed date picker. Value format: 'yyyy-MM-dd'.
 */
@Component({
  selector: 'ds-flatpickr-date',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FlatpickrDateComponent),
      multi: true,
    },
  ],
  templateUrl: './flatpickr-date.component.html',
  styleUrl: './flatpickr-date.component.scss',
})
export class FlatpickrDateComponent implements AfterViewInit, ControlValueAccessor {
  readonly label = input.required<string>();
  readonly min = input<string | undefined>(undefined);
  readonly max = input<string | undefined>(undefined);
  readonly required = input(false);
  readonly placeholder = input('Pilih tanggal');

  protected readonly calendarIcon = LucideCalendarDays;
  protected readonly inputRef = viewChild.required<ElementRef<HTMLInputElement>>('input');
  protected readonly value = signal<string | null>(null);
  protected readonly disabled = signal(false);

  private readonly destroyRef = inject(DestroyRef);
  private flatpickrInstance: FlatpickrInstance | null = null;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    this.initFlatpickr();
  }

  private initFlatpickr(): void {
    const minDate = this.min() ? this.parseDate(this.min()!) ?? undefined : undefined;
    const maxDate = this.max() ? this.parseDate(this.max()!) ?? undefined : undefined;

    this.flatpickrInstance = flatpickr(this.inputRef().nativeElement, {
      locale: Indonesian,
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'j F Y',
      allowInput: false,
      clickOpens: !this.disabled(),
      minDate,
      maxDate,
      defaultDate: this.value() ? (this.parseDate(this.value()!) ?? undefined) : undefined,
      onChange: (dates: Date[]) => {
        const next = dates[0] ? this.formatDate(dates[0]) : null;
        this.value.set(next);
        this.onChange(next);
      },
      onClose: () => {
        this.onTouched();
      },
    });

    this.destroyRef.onDestroy(() => {
      this.flatpickrInstance?.destroy();
      this.flatpickrInstance = null;
    });
  }

  writeValue(val: string | null): void {
    this.value.set(val);
    if (this.flatpickrInstance) {
      const d = val ? this.parseDate(val) : null;
      if (d) {
        this.flatpickrInstance.setDate(d, false);
      } else {
        this.flatpickrInstance.clear(false);
      }
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    const fp = this.flatpickrInstance;
    const input = this.inputRef()?.nativeElement;
    if (!fp || !input) return;
    if (isDisabled) {
      input.setAttribute('disabled', 'true');
      fp.set('clickOpens', false);
    } else {
      input.removeAttribute('disabled');
      fp.set('clickOpens', true);
    }
  }

  private parseDate(iso: string): Date | null {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
